<template>
  <BCard class="twilio-comparison mb-4">
    <h2 class="h4">Compare with Twilio</h2>
    <p>Twilio outcomes alongside our recorded proposals. This comparison does not change SMS sending.</p>
    <form class="twilio-dates" @submit.prevent="load(1)">
      <label>From (UTC)<input v-model="from" required type="date" class="form-control" /></label>
      <label>To (UTC)<input v-model="to" required type="date" class="form-control" :min="from" /></label>
      <button class="btn btn-green" :disabled="loading">Refresh comparison</button>
    </form>
    <p v-if="loading" role="status">Loading Twilio comparison…</p>
    <p v-if="error" role="alert" class="mt-3">{{ error }}</p>
    <template v-if="result">
      <div class="twilio-sync mt-3">
        <strong>{{ result.weekly_enabled ? 'Weekly sync configured' : 'Automatic sync not enabled' }}</strong>
        <span v-if="result.weekly_enabled"> · {{ result.schedule }}</span>
        <p class="mb-1">Last successful API sync (UTC): {{ result.last_success_at || 'None' }}</p>
        <p v-if="result.stale" class="mb-1">A recent successful API sync is missing. Check coverage before drawing conclusions.</p>
        <p v-if="result.last_run" class="mb-1">
          Latest run: {{ result.last_run.source === 'csv' ? 'Manual CSV import' : 'API sync' }} ·
          {{ result.last_run.status }} · {{ result.last_run.finished_at || 'In progress' }} (UTC)
        </p>
        <p v-if="result.last_run?.error" role="alert">{{ result.last_run.error }}</p>
        <p v-if="result.last_run" class="mb-0">{{ result.last_run.coverage_note }}</p>
        <p v-else class="mb-0">No Twilio data has been imported yet.</p>
      </div>
      <p class="mt-3">
        {{ result.internal_coverage.observations }} internal observations in this period;
        {{ result.internal_coverage.without_twilio_match }} without a matching Twilio log.
      </p>
      <p class="mt-3">
        Delivered does not prove an SMS was necessary. A Twilio block does not prove fraud.
        Historical messages without an internal observation cannot be used to measure our detection rate.
      </p>
      <div class="table-responsive">
        <table class="table">
          <caption>Comparison of {{ result.messages.total }} outgoing SMS · {{ result.from }}–{{ result.to }} (UTC)</caption>
          <thead><tr><th>Twilio outcome</th><th>Would allow</th><th>Would pause</th><th>Would block</th><th>No comparison</th></tr></thead>
          <tbody>
            <tr v-for="(label, outcome) in outcomes" :key="outcome">
              <th scope="row">{{ label }}</th>
              <td v-for="decision in decisions" :key="decision">{{ count(outcome, decision) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <details>
        <summary>Inspect individual SMS outcomes</summary>
        <div class="table-responsive mt-3">
          <table class="table">
            <thead><tr><th>Time (UTC) / Message SID</th><th>Twilio result</th><th>Our proposal and recorded reasons</th></tr></thead>
            <tbody>
              <tr v-for="row in result.messages.data" :key="row.sid">
                <td>{{ row.message_at }}<br /><small>{{ row.sid }}</small></td>
                <td>{{ outcomes[row.outcome] }}<br />{{ row.status }} {{ row.error_code || '' }}</td>
                <td>
                  <strong>{{ row.observation_count && row.observation_count > 1 ? 'Ambiguous observation match' : proposals[row.internal_decision] }}</strong>
                  <template v-if="row.internal_observation">
                    <ul class="mb-1">
                      <li v-for="reason in row.internal_observation.reasons" :key="reason">
                        {{ protectionReasonLabel(reason, row.internal_observation.evidence) }}
                      </li>
                    </ul>
                    <p v-if="!row.internal_observation.reasons.length" class="mb-1">
                      {{ row.internal_decision === 'allow' ? 'No rule triggered.' : 'No reason was recorded — investigate this observation.' }}
                    </p>
                    <small>Rule version: {{ row.internal_observation.rule_version }} · Observation #{{ row.internal_observation.id }}</small>
                    <details><summary>Evidence recorded at the time</summary><pre>{{ JSON.stringify(row.internal_observation.evidence, null, 2) }}</pre></details>
                  </template>
                  <p v-else class="mb-0">No reliable recorded reason is available for comparison.</p>
                </td>
              </tr>
              <tr v-if="!result.messages.data.length"><td colspan="3">No imported outgoing SMS in this period.</td></tr>
            </tbody>
          </table>
        </div>
        <nav class="d-flex gap-3 align-items-center" aria-label="Twilio result pages">
          <button class="btn btn-outline-secondary" :disabled="loading || result.messages.current_page <= 1" @click="load(result.messages.current_page - 1)">Previous</button>
          <span>Page {{ result.messages.current_page }} / {{ result.messages.last_page }}</span>
          <button class="btn btn-outline-secondary" :disabled="loading || result.messages.current_page >= result.messages.last_page" @click="load(result.messages.current_page + 1)">Next</button>
        </nav>
      </details>
    </template>
  </BCard>
</template>

<script setup lang="ts">
import type { ProtectionDecision, TwilioComparisonResponse, TwilioOutcome } from '@/types/Models/SmsProtection'
import { isAxiosError } from 'axios'
import { BCard } from 'bootstrap-vue-next'
import { onMounted, ref } from 'vue'
import { protectionReasonLabel } from '@/utils/smsProtection'
import Axios from '@/services/Axios'

const result = ref<TwilioComparisonResponse | null>(null)
const loading = ref(false)
const error = ref('')
const from = ref(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10))
const to = ref(new Date().toISOString().slice(0, 10))
const outcomes: Record<TwilioOutcome, string> = {
  pumping_blocked: 'Pumping blocked (30450)', delivered: 'Delivered', other_failure: 'Other failure', pending: 'Unconfirmed / pending',
}
const decisions: (ProtectionDecision | 'unknown')[] = ['allow', 'would_pause', 'would_block', 'unknown']
const proposals = { allow: 'Would allow', would_pause: 'Would pause', would_block: 'Would block', unknown: 'No internal observation' }
const count = (outcome: TwilioOutcome, decision: ProtectionDecision | 'unknown') =>
  result.value?.summary.find(row => row.outcome === outcome && row.internal_decision === decision)?.total ?? 0

async function load(page = 1) {
  if (loading.value) return
  loading.value = true
  error.value = ''
  try {
    const response = await Axios.get<TwilioComparisonResponse>('/sms-protection/twilio', {
      params: { from: from.value, to: to.value, twilio_page: page },
    })
    result.value = response.data
  } catch (cause) {
    result.value = null
    error.value = isAxiosError(cause) && [404, 503].includes(cause.response?.status || 0)
      ? 'Twilio sync is not connected to this environment yet.'
      : 'Could not load Twilio comparison. Check access and try again.'
  } finally {
    loading.value = false
  }
}
onMounted(() => load())
</script>

<style scoped lang="scss">
.twilio-comparison {
  h2 { color: variables.$dark-green; }
  .twilio-sync { padding: 16px; border: 1px solid variables.$light-border; border-radius: 3px; }
  .twilio-dates {
    display: flex; align-items: end; gap: 16px; flex-wrap: wrap;
    label { display: grid; gap: 8px; margin: 0; }
    .btn { height: 45px; }
  }
}
</style>
